// src/pages/contact/page.tsx

import React from "react";
// FIXED PATH: src/pages/contact/ to src/constants/ -> ../../constants/strings.json
import strings from "../../constants/strings.json";

interface ContactPageProps {
  locale?: string;
}

export default function ContactPage({ locale = "en" }: ContactPageProps): React.ReactElement {
  const localeStrings: any = strings;
  const contact = localeStrings[locale]?.contact ?? localeStrings["en"].contact;

  return (
      <section
          id="contact"
          className="relative bg-gradient-to-b from-white to-gray-50 py-14 md:py-16 px-4"
      >
        {/* Decorative background */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full blur-3xl opacity-20 animate-pulse"></div>

        <div className="relative max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-8 md:mb-10">
          <span className="inline-block px-3 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full bg-gradient-to-r from-[#174143] to-[#3d9ea4] text-white mb-3 shadow-md">
            Get In Touch
          </span>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {contact.page_title}
            </h1>
            <p className="text-sm text-gray-600">
              Visit us or reach out for inquiries
            </p>
          </div>

          {/* Contact Card - Vibrant & Compact */}
          <div className="group relative bg-white rounded-2xl p-6 md:p-8 border border-gray-100 hover:border-transparent shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden">
            {/* Gradient overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#174143]/5 to-[#3d9ea4]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            <div className="relative z-10">
              {/* Office Address Header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-gradient-to-br from-[#174143] to-[#3d9ea4] rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#174143] to-[#3d9ea4] bg-clip-text text-transparent">
                  {contact.office_address_heading}
                </h2>
              </div>

              <div className="space-y-4 mb-6">
                {/* Address */}
                <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors duration-300">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{contact.address_line1}</p>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors duration-300">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">{contact.phone_label}: </span>
                    <a href="tel:+918242449716" className="text-[#174143] hover:text-[#3d9ea4] transition-colors font-medium">
                      0824-2449716/17
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors duration-300">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">{contact.email_label}: </span>
                    <a
                        href={`mailto:${contact.email_address} `}
                        className="text-[#174143] hover:text-[#3d9ea4] transition-colors font-medium"
                    >
                      {contact.email_address}
                    </a>
                  </div>
                </div>
              </div>

              {/* Map Section */}
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#174143]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  {contact.location_heading}
                </h3>
                <div className="relative w-full overflow-hidden rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <iframe
                      title={contact.map_title}
                      src="https://www.google.com/maps?q=1st Floor, Rashi Complex, Kaniyoor Kadaba Taluk, D. K. 574 328&output=embed"
                      className="w-full h-56 md:h-64"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
  );
}